// FormBuilderFieldCard — one field on the builder canvas: a numbered header
// band (drag handle · type icon · title · collapse/IF/move/duplicate/delete
// actions) over a type-specific read-only preview. Scale types (ratingSlider,
// numberSlider) preview as a captioned scale: their two ends carry the low/high
// labels or the range bounds, marks centered between. The grip ARMS a native
// reorder drag of the card root (whole-card ghost; the grip alone would ghost
// 16px) — armed only while the grip is pressed so clicks, previews and the
// header buttons stay untouched; the payload is the row's client id under
// MOVE_DRAG_MIME and the drop gaps bind the landing position. Mouse-only by
// design: the move chevrons remain the keyboard path.

import { Fragment, useState, type CSSProperties, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Icon, IconButton, Input } from '@/components/common';
import { DEV_ONLY } from '@/config/config';
import type { BuilderField } from './utils/builderModel';
import type { CanvasFieldItem } from './utils/canvasItems';
import { chipStyle } from './utils/builderColors';
import { FIELD_LIBRARY, MOVE_DRAG_MIME } from './utils/fieldLibrary';
import {
  basicKeyPrefix,
  basicRecord,
  formElementDefaultProperties,
  type ChoiceOption,
  type FieldImageRef,
  type LibraryElementType,
} from './utils/formElementDefaults';
import { useFieldImageUrl } from './utils/useFieldImageUrl';

interface FormBuilderFieldCardProps {
  field: BuilderField;
  /** Position label — "3" for a top-level field, "5.2" inside a section. */
  badge: string;
  isFirst: boolean;
  isLast: boolean;
  /** Highlighted + shown in the properties panel. */
  selected: boolean;
  /** The IF button's enablement (checkList · has options · single choice). */
  logicEligible: boolean;
  /** Open the conditional-logic drawer for this field. */
  onOpenLogic: () => void;
  /** Conditional-logic follow-up questions nested under this card (recursive —
   *  a level-1 follow-up carries its level-2 rows). Read-only summary rows;
   *  clicking one SELECTS the child into the properties panel. */
  followUps: CanvasFieldItem[];
  /** The page-wide selection — a follow-up row may be the selected field. */
  selectedFieldId: string | null;
  onSelectChild: (fieldId: string) => void;
  onSelect: () => void;
  onToggleCollapse: () => void;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /** This card is the live drag source (canvas ghosts it). */
  dragging: boolean;
  /** A grip drag started/ended — the canvas tracks the dragged id. */
  onDragStateChange: (dragging: boolean) => void;
}

/** Property-indicator flags shown under the card header (design order:
 *  validation states, then media allowances). Icons and label keys reuse the
 *  properties panel's own (ValidationSection ROW_ICON / MediaSection ROWS) so
 *  the card and the panel always tell the same story. Read only is separate —
 *  it renders as a labeled Chip ahead of these icon-only chips. */
const FLAG_SPECS: {
  key: string;
  group: 'validationProperties' | 'mediaProperties';
  icon: string;
  labelKey: string;
  kind: 'val' | 'med';
}[] = [
  {
    key: 'hidden',
    group: 'validationProperties',
    icon: 'eye-off',
    labelKey: 'forms_builder_props_val_hidden',
    kind: 'val',
  },
  {
    key: 'hideOnPdf',
    group: 'validationProperties',
    icon: 'file-x',
    labelKey: 'forms_builder_props_val_hideOnPdf',
    kind: 'val',
  },
  {
    key: 'allowNotes',
    group: 'mediaProperties',
    icon: 'notebook-pen',
    labelKey: 'forms_builder_props_med_allowNotes',
    kind: 'med',
  },
  {
    key: 'allowPictures',
    group: 'mediaProperties',
    icon: 'image',
    labelKey: 'forms_builder_props_med_allowPictures',
    kind: 'med',
  },
  {
    key: 'allowVideos',
    group: 'mediaProperties',
    icon: 'video',
    labelKey: 'forms_builder_props_med_allowVideos',
    kind: 'med',
  },
  {
    key: 'allowDocuments',
    group: 'mediaProperties',
    icon: 'file-text',
    labelKey: 'forms_builder_props_med_allowDocuments',
    kind: 'med',
  },
];

/** Trailing input icon per input-like field type. */
const INPUT_ICON: Partial<Record<LibraryElementType, string>> = {
  dateTime: 'calendar-clock',
  location: 'map-pin',
  companyStructure: 'building-2',
  companyField: 'building',
  dataSource: 'database',
  sendTo: 'send',
  otp: 'shield-check',
};

// User-set text styling for the title/hint, applied only where it differs from
// the type defaults — the model defaults are absolute (#000000ff), so "Default"
// must mean "inherit the theme token" or dark mode would break.
const textStyle = (
  bp: Record<string, unknown>,
  defaults: Record<string, unknown>,
  base: string,
): CSSProperties | undefined => {
  const style: CSSProperties = {};
  const color = bp[`${base}Color`] as string | undefined;
  if (color && color !== defaults[`${base}Color`]) style.color = color;
  const size = bp[`${base}Size`] as string | undefined;
  if (size && size !== defaults[`${base}Size`]) style.fontSize = size;
  const fontType = (bp[`${base}FontType`] as string[]) ?? [];
  if (fontType.includes('bold')) style.fontFamily = "'PlusJakartaSans Bold', sans-serif";
  if (fontType.includes('italic')) style.fontStyle = 'italic';
  if (fontType.includes('underline')) style.textDecoration = 'underline';
  return Object.keys(style).length ? style : undefined;
};

// ratingIcon value → lucide name for the ratingSlider preview. `smile` is no
// longer offered in the panel but stays mapped: a design saved while it was
// should keep rendering as drawn rather than silently falling back to a star.
// Exported: the phone-frame preview renders the same icons so the two surfaces
// can't drift.
export const RATING_PREVIEW_ICON: Record<string, string> = {
  star: 'star',
  heart: 'heart',
  ellipse: 'circle',
  thumbsUp: 'thumbs-up',
  smile: 'smile',
};

const FieldPreview = ({
  type,
  placeholder,
  options,
  ratingIcon,
  ratingCount,
  startText,
  endText,
  image,
  imageName,
}: {
  type: LibraryElementType;
  placeholder?: string;
  options?: ChoiceOption[];
  ratingIcon?: string;
  ratingCount?: number;
  /** Captions for a scale preview's two ends — the rating's low/high labels,
   *  the number slider's range bounds. Empty ones simply don't render. */
  startText?: string;
  endText?: string;
  image?: string;
  imageName?: string;
}) => {
  const { t } = useTranslation();

  // A scale preview: both ends captioned, the marks stretched centered between
  // them. One flex row, so start/end flip under RTL with no extra rules.
  const scale = (marks: ReactNode) => (
    <div className='form-builder-field__scale'>
      {startText && <span className='form-builder-field__scale-end'>{startText}</span>}
      {marks}
      {endText && <span className='form-builder-field__scale-end'>{endText}</span>}
    </div>
  );

  switch (type) {
    case 'checkList':
      return options?.length ? (
        <div className='form-builder-field__chips'>
          {options.map((o, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className='form-builder-field__chip' style={chipStyle(o.itemColor)}>
              {o.responseText || '—'}
            </span>
          ))}
        </div>
      ) : null;
    case 'ratingSlider':
      return scale(
        <div className='form-builder-field__stars'>
          {Array.from({ length: Math.min(10, Math.max(1, ratingCount || 5)) }, (_, i) => (
            <Icon key={i} name={RATING_PREVIEW_ICON[ratingIcon ?? ''] ?? 'star'} size={22} />
          ))}
        </div>,
      );
    case 'numberSlider':
      return scale(<input type='range' className='form-builder-field__slider' disabled />);
    case 'image':
      // A chosen reference image previews as a read-only card (thumb + label +
      // filename + expand affordance); otherwise the capture placeholder.
      return image ? (
        <div className='form-builder-field__ref-image'>
          <img className='form-builder-field__ref-thumb' src={image} alt='' />
          <div className='form-builder-field__ref-meta'>
            <span className='form-builder-field__ref-title'>
              {t('forms_builder_props_basic_image')}
            </span>
            {imageName && <span className='form-builder-field__ref-name'>{imageName}</span>}
          </div>
          <Icon name='maximize-2' size={16} />
        </div>
      ) : (
        <div className='form-builder-field__capture'>
          <Icon name='camera' size={22} />
          {t('forms_builder_preview_photo')}
        </div>
      );
    case 'signature':
      return (
        <div className='form-builder-field__capture'>
          <Icon name='signature' size={22} />
          {t('forms_builder_preview_signature')}
        </div>
      );
    case 'qrCode':
      return (
        <div className='form-builder-field__capture'>
          <Icon name='qr-code' size={22} />
          {t('forms_builder_preview_qr')}
        </div>
      );
    case 'section':
      return <hr className='form-builder-field__divider' />;
    case 'number':
      return <Input placeholder={placeholder || '0'} disabled />;
    default:
      return <Input iconEnd={INPUT_ICON[type]} placeholder={placeholder} disabled size='sm' />;
  }
};

const FormBuilderFieldCard = ({
  field,
  badge,
  isFirst,
  isLast,
  selected,
  logicEligible,
  onOpenLogic,
  followUps,
  selectedFieldId,
  onSelectChild,
  onSelect,
  onToggleCollapse,
  onMove,
  onDuplicate,
  onDelete,
  dragging,
  onDragStateChange,
}: FormBuilderFieldCardProps) => {
  const { t } = useTranslation();
  // Grip-armed drag: the root is draggable only while the grip is pressed.
  const [armed, setArmed] = useState(false);
  const handleDragStart = (e: DragEvent) => {
    // A child card's drag must not bubble into its section card's root (also
    // a drag source) — the outer dragstart would overwrite the dataTransfer.
    e.stopPropagation();
    e.dataTransfer.setData(MOVE_DRAG_MIME, field.id);
    e.dataTransfer.effectAllowed = 'move';
    onDragStateChange(true);
  };
  const handleDragEnd = () => {
    // Fires on drop, Escape-cancel and out-of-window release alike.
    setArmed(false);
    onDragStateChange(false);
  };
  const entry = FIELD_LIBRARY.find((f) => f.type === field.elementType);
  const ruleCount = field.rules.length;

  // Live-bound basics: title in the header, hint above the preview, placeholder
  // inside it (falling back to the library label / nothing when unset).
  const prefix = basicKeyPrefix(field.elementType);
  const bp = basicRecord(field.properties);
  const defaults = basicRecord(formElementDefaultProperties[field.elementType]);
  // Untitled reads as a placeholder rather than falling back to the type name,
  // which looked like a real title. `.trim()` so a whitespace-only title reads as
  // untitled here too — matching what the save-time validator rejects. The type
  // is still conveyed by __type-icon beside it.
  const ownTitle = ((bp[`${prefix}Title`] as string) ?? '').trim();
  const title = ownTitle || t('forms_builder_untitled_field');
  const hint = (bp[`${prefix}HintText`] as string) ?? '';
  const placeholder = (bp.fieldPlaceholder as string) ?? '';
  const required =
    'validationProperties' in field.properties &&
    (field.properties.validationProperties as Record<string, unknown>).required === true;
  // Active indicator flags for the row under the header. Groups are read
  // defensively — types without them (image, section) simply show none.
  const flagGroup = (group: 'validationProperties' | 'mediaProperties') =>
    group in field.properties
      ? ((field.properties as unknown as Record<string, Record<string, unknown>>)[group] as Record<
          string,
          unknown
        >)
      : undefined;
  const readOnly = flagGroup('validationProperties')?.readOnly === true;
  const activeFlags = FLAG_SPECS.filter((f) => flagGroup(f.group)?.[f.key] === true);
  // Reference image (image fields only) — object URL for an unsaved pick, else
  // resolved from the saved media id. Unconditional call: hooks can't be gated
  // on the element type.
  const fieldImageUrl = useFieldImageUrl(
    bp.fieldImage as FieldImageRef | null | undefined,
    bp.fieldImageFile,
  );

  // Scale-preview end captions: the rating's low/high labels, the number
  // slider's range bounds. The bounds render as bare numbers — data, not copy,
  // so no i18n key.
  let scaleStart: string | undefined;
  let scaleEnd: string | undefined;
  if (field.elementType === 'ratingSlider') {
    scaleStart = bp.lowLabel as string;
    scaleEnd = bp.highLabel as string;
  } else if (field.elementType === 'numberSlider') {
    scaleStart = String(bp.minRange ?? '');
    scaleEnd = String(bp.maxRange ?? '');
  }

  // Card-body tint from layoutProperties.backgroundColor — applied only when it
  // differs from the type default and Transparent is off ("Default = inherit
  // theme": the absolute #ffffffff default must not paint dark mode).
  let bodyStyle: CSSProperties | undefined;
  if ('layoutProperties' in field.properties) {
    const lp = field.properties.layoutProperties as Record<string, unknown>;
    const lpDefaults = (formElementDefaultProperties[field.elementType] as Record<string, unknown>)
      .layoutProperties as Record<string, unknown>;
    const bg = lp.backgroundColor as string | undefined;
    const rgb = (h?: string) => (h ?? '').toLowerCase().slice(0, 7);
    if (bg && rgb(bg) !== rgb(lpDefaults.backgroundColor as string) && lp.transparent !== true) {
      bodyStyle = { background: rgb(bg) };
    }
  }

  return (
    // Card click selects the field (opens the properties panel); the trailing
    // actions stop propagation so structural actions don't change selection.
    <div
      className={[
        'form-builder-field',
        selected && 'form-builder-field--selected',
        dragging && 'form-builder-field--dragging',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onSelect}
      draggable={armed}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      // Disarm every way the grip press can end without a drag. Root-level on
      // purpose: a grip-level mouseleave could race the browser's few-px
      // dragstart threshold, and once a drag is live mouse events stop firing,
      // so neither of these can kill it.
      onMouseUp={() => setArmed(false)}
      onMouseLeave={() => setArmed(false)}
    >
      <div className='form-builder-field__head'>
        <span
          className='form-builder-field__grip'
          aria-hidden='true'
          onMouseDown={() => setArmed(true)}
        >
          <Icon name='grip-vertical' size={15} />
        </span>
        <span className='form-builder-field__num'>{badge}</span>
        <span className='form-builder-field__type-icon'>
          <Icon name={entry?.icon ?? 'type'} size={14} />
        </span>
        <span
          className={[
            'form-builder-field__title',
            !ownTitle && 'form-builder-field__title--untitled',
          ]
            .filter(Boolean)
            .join(' ')}
          style={textStyle(bp, defaults, `${prefix}Title`)}
        >
          {title}
        </span>
        {required && (
          <span className='form-builder-field__req' aria-hidden='true'>
            *
          </span>
        )}

        <div className='form-builder-field__actions' onClick={(e) => e.stopPropagation()}>
          <IconButton
            icon='chevrons-down-up'
            variant='ghost'
            size='sm'
            aria-label={t(
              field.collapsed ? 'forms_builder_field_expand' : 'forms_builder_field_collapse',
            )}
            onClick={onToggleCollapse}
          />
          {/* Conditional logic is not shipped yet: outside DEV_ONLY the button
           * stays visible but inert, and the env gate outranks the eligibility
           * one so an otherwise-eligible Choice List never claims the
           * eligibility hint. Enabled only for a single-choice checkList with
           * options; the brand tint marks a field that already carries rules
           * (:disabled outranks --active, so a gated card still reads grey). */}
          <button
            type='button'
            className={[
              'form-builder-field__logic',
              ruleCount > 0 && 'form-builder-field__logic--active',
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={!DEV_ONLY || !logicEligible}
            title={
              !DEV_ONLY
                ? t('forms_builder_logic_unavailable_hint')
                : !logicEligible
                  ? t('forms_builder_logic_disabled_hint')
                  : undefined
            }
            onClick={onOpenLogic}
          >
            <Icon name='git-branch' size={14} />
            {t('forms_builder_field_logic')}
          </button>
          <IconButton
            icon='chevron-up'
            variant='ghost'
            size='sm'
            aria-label={t('forms_builder_field_move_up')}
            disabled={isFirst}
            onClick={() => onMove(-1)}
          />
          <IconButton
            icon='chevron-down'
            variant='ghost'
            size='sm'
            aria-label={t('forms_builder_field_move_down')}
            disabled={isLast}
            onClick={() => onMove(1)}
          />
          <IconButton
            icon='copy'
            variant='ghost'
            size='sm'
            aria-label={t('forms_builder_field_duplicate')}
            // Exactly one per form — a copy could never be saved.
            disabled={field.elementType === 'companyStructure'}
            onClick={onDuplicate}
          />
          <IconButton
            icon='trash-2'
            variant='ghost'
            size='sm'
            className='form-builder-field__delete'
            aria-label={t('forms_builder_field_delete')}
            onClick={onDelete}
          />
        </div>
      </div>

      {ruleCount > 0 && (
        <div className='form-builder-field__logic-banner'>
          <Icon name='git-branch' size={12} />
          <span className='form-builder-field__logic-count'>
            {t('forms_builder_logic_banner_count', { count: ruleCount })}
          </span>
          {/* The count stays readable everywhere — it reports real saved rules;
           * only the drawer entry is DEV_ONLY. */}
          {DEV_ONLY && (
            <button
              type='button'
              className='form-builder-field__logic-view'
              onClick={(e) => {
                e.stopPropagation();
                onOpenLogic();
              }}
            >
              <Icon name='eye' size={12} />
              {t('forms_builder_logic_view')}
            </button>
          )}
        </div>
      )}

      {/* Property-indicator flags — outside the collapse guard (like the
       * logic banner) so a collapsed card still scans. Icon-only chips are
       * indicative, not interactive; labels ride as tooltip + aria-label. */}
      {(readOnly || activeFlags.length > 0) && (
        <div className='form-builder-field__flags'>
          {readOnly && <Chip iconStart='lock'>{t('forms_builder_props_val_readOnly')}</Chip>}
          {activeFlags.map((f) => (
            <span
              key={f.key}
              className={`form-builder-field__flag form-builder-field__flag--${f.kind}`}
              role='img'
              title={t(f.labelKey)}
              aria-label={t(f.labelKey)}
            >
              <Icon name={f.icon} size={11} />
            </span>
          ))}
        </div>
      )}

      {!field.collapsed && (
        <div className='form-builder-field__body' style={bodyStyle}>
          {hint && (
            <p
              className='form-builder-field__hint'
              style={textStyle(bp, defaults, `${prefix}HintText`)}
            >
              {hint}
            </p>
          )}
          <FieldPreview
            type={field.elementType}
            placeholder={placeholder}
            options={
              field.elementType === 'checkList' ? (bp.optionList as ChoiceOption[]) : undefined
            }
            ratingIcon={bp.ratingIcon as string | undefined}
            ratingCount={bp.ratingPointers as number | undefined}
            startText={scaleStart}
            endText={scaleEnd}
            image={fieldImageUrl}
            imageName={bp.fieldImageFile instanceof File ? bp.fieldImageFile.name : undefined}
          />

          {followUps.length > 0 && (
            <div className='form-builder-field__follow-ups'>
              <span className='form-builder-field__follow-ups-title'>
                <Icon name='corner-down-right' size={13} />
                {t('forms_builder_logic_follow_up_title')}
              </span>
              <FollowUpRows
                host={field}
                items={followUps}
                selectedFieldId={selectedFieldId}
                onSelectChild={onSelectChild}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** The read-only follow-up summary rows: "If <answer>" chip · type icon ·
 *  title · (rules-count badge) · type label; level-2 rows nest behind a dotted
 *  lead. Clicking a row selects the CHILD into the properties panel — that is
 *  the only affordance (structure is managed in the logic drawer). */
const FollowUpRows = ({
  host,
  items,
  selectedFieldId,
  onSelectChild,
}: {
  host: BuilderField;
  items: CanvasFieldItem[];
  selectedFieldId: string | null;
  onSelectChild: (fieldId: string) => void;
}) => {
  const { t } = useTranslation();
  const hostOptions =
    ((host.properties.basicProperties as Record<string, unknown>).optionList as ChoiceOption[]) ??
    [];

  return (
    <>
      {items.map((up) => {
        const rule = host.rules.find(
          (r) => r.action?.kind === 'ask_a_question' && r.action.childId === up.field.id,
        );
        const answer = hostOptions.find((o) => o.value === rule?.matchingValue);
        const entry = FIELD_LIBRARY.find((f) => f.type === up.field.elementType);
        const title = ((basicRecord(up.field.properties).fieldTitle as string) ?? '').trim();
        return (
          <Fragment key={up.field.id}>
            <button
              type='button'
              className={[
                'form-builder-field__follow-up',
                up.field.id === selectedFieldId && 'form-builder-field__follow-up--selected',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={(e) => {
                e.stopPropagation();
                onSelectChild(up.field.id);
              }}
            >
              <span
                className='form-builder-field__follow-up-chip'
                style={answer ? chipStyle(answer.itemColor) : undefined}
              >
                {t('forms_builder_logic_if_chip', { answer: answer?.responseText ?? '—' })}
              </span>
              <span className='form-builder-field__follow-up-icon'>
                <Icon name={entry?.icon ?? 'type'} size={14} />
              </span>
              <span
                className={[
                  'form-builder-field__follow-up-title',
                  !title && 'form-builder-field__follow-up-title--untitled',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {title || t('forms_builder_untitled_field')}
              </span>
              <span className='form-builder-field__follow-up-end'>
                {up.field.rules.length > 0 && (
                  <span className='form-builder-field__follow-up-rules'>
                    <Icon name='git-branch' size={13} />
                    {up.field.rules.length}
                  </span>
                )}
                <span className='form-builder-field__follow-up-type'>
                  {entry ? t(entry.labelKey) : up.field.elementType}
                </span>
              </span>
            </button>
            {up.followUps.length > 0 && (
              <div className='form-builder-field__follow-up-children'>
                <FollowUpRows
                  host={up.field}
                  items={up.followUps}
                  selectedFieldId={selectedFieldId}
                  onSelectChild={onSelectChild}
                />
              </div>
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default FormBuilderFieldCard;
